<template>
  <div v-if="isReady" class="customer-service-wrapper">
    <button v-if="!panelVisible" class="customer-service-fab" @click="openPanel" :aria-label="$t('common.在线客服')">
      <img class="fab-logo" src="@/assets/images/hamilton-lane-logo-transparent.png" alt="" />
      <span v-if="unreadCount > 0" class="fab-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <div v-if="panelVisible" class="customer-service-panel">
      <div class="panel-header">
        <div class="header-left">
          <img class="avatar-logo" src="@/assets/images/hamilton-lane-logo-transparent.png" :alt="$t('common.品牌名')" />
          <div>
            <div class="title">{{ $t('common.在线客服') }}</div>
            <div class="status"><span class="online-dot"></span>{{ $t('common.在线') }}</div>
          </div>
        </div>
        <div class="header-actions">
          <button class="icon-btn close" @click="closePanel">×</button>
        </div>
      </div>

      <div ref="messageListRef" class="panel-body">
        <div v-for="msg in messages" :key="msg.id" class="message-row" :class="msg.role">
          <div class="bubble" :class="{ image: msg.type === 'image' }">
            <img v-if="msg.type === 'image'" :src="msg.imageUrl" alt="" class="chat-image" />
            <span v-else>{{ msg.isWelcome ? $t('common.客服欢迎语') : msg.text }}</span>
          </div>
          <div class="time">{{ formatTime(msg.time) }}</div>
        </div>
      </div>

      <div class="panel-footer">
        <input ref="uploadInputRef" type="file" accept="image/*" class="upload-input" @change="onUploadImage" />
        <button class="upload-btn" @click="triggerUpload">+</button>
        <input
          v-model="draftMessage"
          class="message-input"
          type="text"
          :placeholder="$t('common.输入消息占位')"
          @keyup.enter="sendMessage"
        />
        <button class="send-btn" @click="sendMessage">{{ $t('common.发送') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from '@/services/api.js'
import Pusher from 'pusher-js'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'CustomerServiceButton',
  data() {
    return {
      isReady: true,
      panelVisible: false,
      draftMessage: '',
      messages: [],
      maxUploadSize: 5 * 1024 * 1024,
      sending: false,
      lastMessageId: null,
      pusher: null,
      channel: null,
      userId: null,
      pollTimer: null,
      unreadCount: 0,
      unreadTimer: null
    }
  },
  mounted() {
    this.checkUnread()
    this.unreadTimer = setInterval(() => this.checkUnread(), 10000)
  },
  beforeUnmount() {
    this.disconnectSocket()
    this.stopPolling()
    if (this.unreadTimer) { clearInterval(this.unreadTimer); this.unreadTimer = null }
  },
  methods: {
    async checkUnread() {
      if (this.panelVisible) return
      if (!localStorage.getItem('wallet_token')) return
      try {
        const res = await request('/customer-service/unread-count')
        if (res && res.success) this.unreadCount = res.data.count || 0
      } catch (e) { /* ignore */ }
    },
    async markAsRead() {
      if (this.lastMessageId) {
        localStorage.setItem('cs_last_read_id', String(this.lastMessageId))
      }
      this.unreadCount = 0
      if (!localStorage.getItem('wallet_token')) return
      try {
        await request('/customer-service/mark-read', { method: 'POST', body: JSON.stringify({}) })
      } catch (e) { /* ignore */ }
    },
    async openPanel() {
      this.panelVisible = true
      await this.loadConversation()
      this.markAsRead()
      this.connectSocket()
      this.startPolling()
    },
    closePanel() {
      this.markAsRead()
      this.panelVisible = false
      this.disconnectSocket()
      this.stopPolling()
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => this.pollNewMessages(), 3000)
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    },
    async pollNewMessages() {
      if (!this.panelVisible) return
      try {
        const res = await request(`/customer-service/conversation?_=${Date.now()}`)
        if (!res || res.notModified) return
        if (!res.success) return
        const mapped = this.mapMessages(res.data.messages)
        const lastKnown = this.lastMessageId
        const newMsgs = lastKnown
          ? mapped.filter(m => m.id > lastKnown)
          : []
        if (newMsgs.length > 0) {
          for (const nm of newMsgs) {
            if (!this.messages.some(m => m.id === nm.id)) {
              this.messages.push(nm)
            }
          }
          this.lastMessageId = mapped[mapped.length - 1].id
          this.markAsRead()
          this.$nextTick(this.scrollToBottom)
        }
      } catch (e) { /* ignore */ }
    },
    /** 将后端消息格式转为前端展示格式 */
    mapMessages(apiMessages) {
      return (apiMessages || []).map(msg => ({
        id: msg.id,
        role: msg.sender_type === 'user' ? 'user' : 'support',
        type: msg.type || 'text',
        text: msg.type === 'image' ? '' : msg.content,
        imageUrl: msg.type === 'image' ? msg.content : '',
        time: new Date(msg.created_at).getTime()
      }))
    },
    /** 加载对话和历史消息 */
    async loadConversation() {
      try {
        const res = await request(`/customer-service/conversation?_=${Date.now()}`)
        if (res.success) {
          const mapped = this.mapMessages(res.data.messages)
          const welcomeTime = mapped.length ? mapped[0].time - 1000 : Date.now()
          this.messages = [
            { id: 'welcome', role: 'support', type: 'text', isWelcome: true, time: welcomeTime },
            ...mapped
          ]
          if (mapped.length) {
            this.lastMessageId = mapped[mapped.length - 1].id
          }
          this.$nextTick(this.scrollToBottom)
        }
      } catch (e) {
        console.error('加载客服对话失败:', e)
        this.messages = [{ id: 'welcome', role: 'support', type: 'text', isWelcome: true, time: Date.now() }]
      }
    },
    /** 发送文字消息到后端 */
    async sendMessage() {
      const content = String(this.draftMessage || '').trim()
      if (!content || this.sending) return
      this.sending = true

      // 先乐观地显示在界面上
      const tempId = 'temp_' + Date.now()
      this.messages.push({ id: tempId, role: 'user', type: 'text', text: content, time: Date.now() })
      this.draftMessage = ''
      this.$nextTick(this.scrollToBottom)

      try {
        const socketId = this.pusher?.connection?.socket_id
        const res = await request('/customer-service/send', {
          method: 'POST',
          body: JSON.stringify({ content, type: 'text' }),
          headers: socketId ? { 'X-Socket-ID': socketId } : {}
        })
        if (res.success && res.data) {
          const idx = this.messages.findIndex(m => m.id === tempId)
          if (idx !== -1) {
            this.messages[idx].id = res.data.id
            this.messages[idx].time = new Date(res.data.created_at).getTime()
            this.lastMessageId = res.data.id
          }
        }
      } catch (e) {
        console.error('发送消息失败:', e)
        // 发送失败则移除临时消息
        const idx = this.messages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.messages.splice(idx, 1)
      }
      this.sending = false
    },
    triggerUpload() {
      const uploadInput = this.$refs.uploadInputRef
      if (uploadInput) uploadInput.click()
    },
    /** 上传图片并发送图片消息 */
    async onUploadImage(event) {
      const input = event.target
      const file = input && input.files && input.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) { input.value = ''; return }
      if (file.size > this.maxUploadSize) { input.value = ''; return }
      input.value = ''

      try {
        // 第1步: 上传图片文件
        const formData = new FormData()
        formData.append('image', file)
        const uploadRes = await request('/customer-service/upload-image', {
          method: 'POST',
          body: formData
        })
        if (uploadRes.success) {
          const imageUrl = uploadRes.data.url
          // 第2步: 发送图片消息
          const socketId = this.pusher?.connection?.socket_id
          const sendRes = await request('/customer-service/send', {
            method: 'POST',
            body: JSON.stringify({ content: imageUrl, type: 'image' }),
            headers: socketId ? { 'X-Socket-ID': socketId } : {}
          })
          if (sendRes.success && sendRes.data) {
            this.messages.push({
              id: sendRes.data.id, role: 'user', type: 'image', imageUrl, time: new Date(sendRes.data.created_at).getTime()
            })
            this.lastMessageId = sendRes.data.id
            this.$nextTick(this.scrollToBottom)
          }
        }
      } catch (e) {
        console.error('上传图片失败:', e)
      }
    },
    /** 连接 WebSocket（Pusher） */
    connectSocket() {
      if (this.pusher) return // 已连接

      // 获取用户 ID
      try {
        const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
        this.userId = userInfo.id
      } catch (e) { /* ignore */ }

      if (!this.userId) {
        console.warn('客服 WebSocket: 无法获取用户 ID，跳过连接')
        return
      }

      const walletAddress = localStorage.getItem('wallet_address') ||
                            localStorage.getItem('address') ||
                            sessionStorage.getItem('address') || ''
      const token = localStorage.getItem('wallet_token') || ''

      const pusherKey = window.__PUSHER_APP_KEY__ || process.env.VUE_APP_PUSHER_APP_KEY || 'app-key'
      const pusherHost = window.__PUSHER_HOST__ || process.env.VUE_APP_PUSHER_HOST || new URL(API_BASE_URL).hostname
      const pusherPort = window.__PUSHER_PORT__ || process.env.VUE_APP_PUSHER_PORT || 6001
      const pusherScheme = window.__PUSHER_SCHEME__ || process.env.VUE_APP_PUSHER_SCHEME || 'https'
      const pusherCluster = window.__PUSHER_CLUSTER__ || process.env.VUE_APP_PUSHER_APP_CLUSTER || 'mt1'

      this.pusher = new Pusher(pusherKey, {
        wsHost: pusherHost,
        wsPort: pusherPort,
        wssPort: pusherPort,
        forceTLS: pusherScheme === 'https',
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        cluster: pusherCluster,
        authEndpoint: API_BASE_URL + '/api/broadcasting/auth',
        auth: {
          headers: {
            'Authorization': 'Bearer ' + token,
            'X-Wallet-Address': walletAddress,
            'Accept': 'application/json'
          }
        }
      })

      const channelName = 'private-customer-service.user.' + this.userId
      this.channel = this.pusher.subscribe(channelName)

      this.channel.bind('new.cs.message', (data) => {
        // 收到新消息
        const msg = data.message
        // 避免添加重复消息
        if (this.messages.some(m => m.id === msg.id)) return

        this.messages.push({
          id: msg.id,
          role: msg.sender_type === 'user' ? 'user' : 'support',
          type: msg.type || 'text',
          text: msg.type === 'image' ? '' : msg.content,
          imageUrl: msg.type === 'image' ? msg.content : '',
          time: new Date(msg.created_at).getTime()
        })
        this.lastMessageId = msg.id
        this.markAsRead()
        this.$nextTick(this.scrollToBottom)
      })

      this.channel.bind('pusher:subscription_error', (status) => {
        console.error('客服 WebSocket 订阅失败:', status)
      })
    },
    /** 断开 WebSocket */
    disconnectSocket() {
      if (this.channel) {
        this.channel.unbind_all()
        this.channel = null
      }
      if (this.pusher) {
        this.pusher.disconnect()
        this.pusher = null
      }
    },
    scrollToBottom() {
      const el = this.$refs.messageListRef
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(ts) {
      const d = new Date(ts)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.customer-service-wrapper {
  z-index: 9999;
}

.customer-service-fab {
  position: fixed;
  right: 16px;
  bottom: 96px;
  width: 56px;
  height: 56px;
  border: 1px solid rgba(88, 126, 167, 0.5);
  border-radius: 50%;
  background:
    radial-gradient(circle at 32% 25%, rgba(47, 241, 210, 0.2), transparent 40%),
    linear-gradient(180deg, rgba(13, 30, 52, 0.96), rgba(9, 21, 39, 0.98));
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(3, 10, 23, 0.46), 0 0 0 1px rgba(47, 241, 210, 0.18) inset;
}

.customer-service-fab .fab-logo {
  width: 34px;
  height: 28px;
  object-fit: cover;
  object-position: left center;
  display: block;
}

.fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid rgba(9, 21, 39, 0.95);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.5);
  animation: badge-pulse 2s ease-in-out infinite;
}

@keyframes badge-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.customer-service-panel {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(360px, calc(100vw - 24px));
  height: min(560px, calc(100vh - 120px));
  background: linear-gradient(180deg, #0c203b 0%, #0a1a31 100%);
  border: 1px solid rgba(88, 126, 167, 0.4);
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.45);
}

.panel-header {
  height: 86px;
  padding: 14px;
  background:
    radial-gradient(circle at 10% 10%, rgba(47, 241, 210, 0.26), transparent 40%),
    linear-gradient(180deg, rgba(17, 36, 62, 0.98), rgba(11, 26, 46, 0.98));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-logo {
  width: 44px;
  height: 36px;
  border-radius: 10px;
  padding: 4px;
  background: rgba(10, 22, 40, 0.85);
  border: 1px solid rgba(20, 125, 170, 0.45);
  object-fit: cover;
  object-position: left center;
  display: block;
}

.title {
  font-size: 20px;
  line-height: 1;
  font-weight: 700;
}

.status {
  margin-top: 6px;
  color: #b8f7ea;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.online-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2ff1d2;
  box-shadow: 0 0 8px rgba(47, 241, 210, 0.45);
}

.header-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.16);
  color: #fff;
  cursor: pointer;
  line-height: 1;
}

.icon-btn.close {
  font-size: 18px;
}

.panel-body {
  flex: 1;
  padding: 14px;
  overflow-y: auto;
}

.message-row {
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.message-row.support {
  align-items: flex-start;
}

.message-row.user {
  align-items: flex-end;
}

.bubble {
  max-width: 85%;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-wrap;
}

.bubble.image {
  padding: 6px;
}

.chat-image {
  display: block;
  max-width: 180px;
  max-height: 180px;
  border-radius: 10px;
}

.message-row.support .bubble {
  background: rgba(255, 255, 255, 0.08);
  color: #e5e7eb;
}

.message-row.user .bubble {
  background: linear-gradient(135deg, #1ef2d7, #59dcff);
  color: #07222c;
}

.time {
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.panel-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.8);
}

.upload-input {
  display: none;
}

.upload-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #1e293b;
  color: #e5e7eb;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.message-input {
  flex: 1;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #334155;
  color: #e5e7eb;
  padding: 0 12px;
  font-size: 14px;
}

.message-input::placeholder {
  color: #94a3b8;
}

.send-btn {
  height: 42px;
  min-width: 74px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #1ef2d7, #59dcff);
  color: #07222c;
  font-weight: 600;
}

@media (max-width: 768px) {
  .title {
    font-size: 18px;
  }

  .customer-service-panel {
    width: calc(100vw - 20px);
    height: min(600px, calc(100vh - 90px));
  }
}

@media (min-width: 769px) {
  .customer-service-fab {
    bottom: 32px;
    right: 32px;
  }
  .customer-service-panel {
    width: 400px;
    height: min(640px, calc(100vh - 80px));
  }
}
</style>
